/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Avatar,
  ListItem,
  CircularProgress,
} from "@mui/material";
import SearchField from "src/components/shared/atoms/SearchField";
import { useLazyQuery } from "@apollo/client";
import GET_USER from "src/graphql/query/users/getUser";
import { enqueueSnackbar } from "notistack";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import Progress from "src/components/shared/atoms/circularProgress/CircularProgress";
import useStyles from "./Styles";

interface RevokeAgentsProps {
  increasedCounts: number; // Max agents user can restore
  currentCount: number;
  selectedCount: number;
  handleDeleteAgentsData: any;
  handleCancel: any;
  loading: boolean;
}

const RevokeAgents: React.FC<RevokeAgentsProps> = ({
  increasedCounts,
  currentCount,
  selectedCount,
  handleDeleteAgentsData,
  handleCancel,
  loading,
}) => {
  const [inviteList, setInviteList] = useState<any[]>([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState<any[]>([]);
  const [showError, setShowError] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [searchListAgents, setSearchListAgents] = useState<any[]>([]);
  const [listAgents, setListAgents] = useState<any[]>([]);
  const {
    mainSuperAdminId,
    panelType,
    agentId: userId,
  } = useActiveUserDetail();
  const [pagination, setPagination] = useState({
    page: 0,
    hasNextPage: false,
    limit: 10,
  });
  const [searchListAgentsFlag, setSearchListAgentsFlag] = useState(false);

  const CURSOR = 0;

  const fetchUsers = (data: any) => {
    const rawData = data.users
      .filter((user: any) => user.id !== userId)
      .map((user: any) => ({
        ...user,
        isSelected: false,
      }));

    const prevSelectedIds = [
      ...selectedAgentIds.map((agent) => agent.id),
      ...inviteList.map((agent: { id: any }) => agent.id),
    ];

    const updatedData = rawData.map((item: { id: any }) =>
      prevSelectedIds.includes(item.id) ? { ...item, isSelected: true } : item,
    );

    if (!searchListAgentsFlag) {
      setListAgents((prev) => [...prev, ...updatedData]);
    } else {
      setSearchListAgents((prev) => [...prev, ...updatedData]);
    }

    setPagination({
      page: pagination.page + 1,
      limit: 10,
      hasNextPage: data.hasNextPage,
    });
  };

  const [
    getAgents,
    {
      loading: getAgentsQueryLoading,
      error: getAgentsQueryError,
      data: getAgentsQueryResult,
    },
  ] = useLazyQuery(GET_USER, { fetchPolicy: "network-only" });

  useEffect(() => {
    if (getAgentsQueryError) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    }
  }, [getAgentsQueryError]);

  useEffect(() => {
    if (getAgentsQueryResult?.getUsers?.users) {
      fetchUsers(getAgentsQueryResult.getUsers);
    }
  }, [getAgentsQueryResult]);

  const loadMoreDataHandle = (e: React.SyntheticEvent) => {
    const target = e.target as HTMLElement;
    const bottom =
      target.scrollHeight - target.clientHeight - target.scrollTop < 1;
    if (bottom && pagination.hasNextPage) {
      getAgents({
        variables: {
          mainSuperAdminId,
          cursor:
            pagination.page === CURSOR
              ? pagination.limit * pagination.page
              : Number(listAgents.at(-1).id),
          limit: pagination.limit,
          query: "",
          panelType,
          deletedUsersOnly: 1,
        },
      });
    }
  };

  useEffect(() => {
    getAgents({
      variables: {
        mainSuperAdminId,
        cursor: CURSOR,
        limit: pagination.limit,
        query: "",
        panelType,
        deletedUsersOnly: 1,
      },
    });
  }, []);

  const handleSelectAll = (flag: boolean) => {
    const list = searchListAgentsFlag ? [...searchListAgents] : [...listAgents];
    // eslint-disable-next-line no-return-assign
    list.forEach((el) => (el.isSelected = flag));
    if (!searchListAgentsFlag) setListAgents(list);
    else setSearchListAgents(list);
    setAllSelected(flag);

    const selected = flag ? list : [];
    setSelectedAgentIds(selected);
  };

  const handleCheckListItem = (item: any) => {
    // Determine which list to update
    const targetList = searchListAgentsFlag
      ? [...searchListAgents]
      : [...listAgents];

    // Toggle isSelected for this item
    const updatedList = targetList.map((a) =>
      a.id === item.id ? { ...a, isSelected: !a.isSelected } : a,
    );

    // Update the correct state
    if (searchListAgentsFlag) setSearchListAgents(updatedList);
    else setListAgents(updatedList);

    // Update selectedAgentIds based on the updated list only
    const selected = updatedList.filter((i) => i.isSelected);
    setSelectedAgentIds(selected);

    // Update select-all checkbox
    setAllSelected(selected.length === updatedList.length);
  };

  const handleSubmit = () => {
    if (selectedAgentIds.length > increasedCounts) {
      setShowError(true);
      enqueueSnackbar(
        `You can only restore up to ${increasedCounts} agent${
          increasedCounts > 1 ? "s" : ""
        }.`,
        { variant: "error" },
      );
      return;
    }

    const updatedList = [...inviteList, ...selectedAgentIds];
    setInviteList(updatedList);
    handleDeleteAgentsData(selectedAgentIds);
  };

  useEffect(() => {
    if (showError) {
      const timer = setTimeout(() => setShowError(false), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [showError]);

  const { classes } = useStyles();
  const [text, setText] = useState("");
  const [debouncedText, setDebouncedText] = useState("");

  const searchBarHandle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
  };

  useEffect(() => {
    const timerId = setTimeout(() => setDebouncedText(text), 1000);
    return () => clearTimeout(timerId);
  }, [text]);

  useEffect(() => {
    setSearchListAgents([]);
    if (debouncedText !== "") {
      setSearchListAgentsFlag(true);
      getAgents({
        variables: {
          mainSuperAdminId,
          cursor: CURSOR,
          limit: pagination.limit,
          query: debouncedText,
          panelType,
          deletedUsersOnly: 1,
        },
      });
    } else {
      setSearchListAgentsFlag(false);
      getAgents({
        variables: {
          mainSuperAdminId,
          cursor: CURSOR,
          limit: pagination.limit,
          query: "",
          panelType,
          deletedUsersOnly: 1,
        },
      });
    }
  }, [debouncedText]);

  const searchFieldProps = {
    value: text,
    onChange: searchBarHandle,
    placeholder: "Search",
    fieldClass: classes.fieldStyle,
    showEndIcon: true,
    endIcon: getAgentsQueryLoading && (
      <CircularProgress size={20} sx={{ mr: 1 }} />
    ),
  };

  return (
    <Box>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Instruction */}
        {increasedCounts > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="#28a745">
              You currently have <strong>{currentCount}</strong> user
              {currentCount > 1 ? "s" : ""}. You have selected{" "}
              <strong>{selectedCount}</strong> user
              {selectedCount > 1 ? "s" : ""}. You can restore up to{" "}
              <strong>{increasedCounts}</strong> user
              {increasedCounts > 1 ? "s" : ""}. You may now revoke old users if
              needed.
            </Typography>
          </Box>
        )}

        <SearchField {...searchFieldProps} />

        <Box sx={{ mt: showError ? "-8px" : "10px" }}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => handleSelectAll(!allSelected)}
          />
          <Box component="span" className={classes.selectAll}>
            Select all
          </Box>
        </Box>

        <Box onScroll={loadMoreDataHandle} className={classes.loadMoreData}>
          {!getAgentsQueryLoading && listAgents.length === 0 && (
            <div className="m-auto text-center">
              <p className="font-small">No record found</p>
            </div>
          )}

          {!searchListAgentsFlag &&
            listAgents.map((item) => (
              <ListItem alignItems="center" sx={{ p: 0 }} key={item.id}>
                <input
                  type="checkbox"
                  checked={item.isSelected}
                  onChange={() => handleCheckListItem(item)}
                />
                <Avatar
                  alt={item.name}
                  className={classes.image}
                  src={item.picture}
                />
                <Typography variant="subtitle2" className={classes.name}>
                  {item.name}
                </Typography>
              </ListItem>
            ))}

          {searchListAgentsFlag &&
            searchListAgents.map((item) => (
              <ListItem alignItems="center" sx={{ p: 0 }} key={item.id}>
                <input
                  type="checkbox"
                  checked={item.isSelected}
                  onChange={() => handleCheckListItem(item)}
                />
                <Avatar
                  alt={item.name}
                  className={classes.image}
                  src={item.picture}
                />
                <Typography variant="subtitle2" className={classes.name}>
                  {item.name}
                </Typography>
              </ListItem>
            ))}

          {!debouncedText && getAgentsQueryLoading && <Progress />}
        </Box>

        {showError && (
          <Typography variant="caption" color="error">
            You can only restore up to {increasedCounts} agent
            {increasedCounts > 1 ? "s" : ""}.
          </Typography>
        )}

        <Box className={classes.submit}>
          <Button
            variant="contained"
            className={classes.cancelBtn}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={loading || selectedAgentIds.length > increasedCounts}
            className={classes.submitButton}
          >
            {loading ? (
              <span>
                Processing...{" "}
                <CircularProgress size={13} sx={{ color: "white" }} />
              </span>
            ) : selectedAgentIds.length === 0 ? (
              "Continue without selection"
            ) : selectedAgentIds.length > increasedCounts ? (
              `Max ${increasedCounts} Allowed`
            ) : (
              "Restore Selected"
            )}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default RevokeAgents;
